/**
 * AccountDeactivatePage (Doc 55) — Danger zone: self-deactivate account.
 *
 * Soft delete v1: SET is_active=false + revoke all sessions. Admin có thể
 * reactivate. Hard delete defer v1.1.
 */
import { AlertTriangle, Loader2, ShieldOff } from "lucide-react";
import * as React from "react";

import { settingsApi } from "@/api/settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/stores/auth";
import { useUIStore } from "@/stores/ui";

export function AccountDeactivatePage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const [password, setPassword] = React.useState("");
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleDeactivate = async (): Promise<void> => {
    if (!acknowledged || !password) return;
    const sure = window.confirm(
      "Xác nhận lần cuối: tài khoản sẽ bị deactivate ngay lập tức và bạn " +
        "sẽ bị đăng xuất khỏi tất cả thiết bị. Tiếp tục?",
    );
    if (!sure) return;

    setSubmitting(true);
    setError(null);
    try {
      await settingsApi.deactivateAccount(password);
      pushToast({
        variant: "success",
        title: "Đã deactivate tài khoản",
        description: "Đang đăng xuất...",
      });
      // Force redirect login + clear local auth state
      setTimeout(() => {
        clearAuth();
        window.location.hash = "/";
        window.location.reload();
      }, 1200);
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : typeof e === "object" && e && "message" in e
            ? String((e as { message?: string }).message)
            : String(e);
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <ShieldOff className="size-5" /> Tài khoản
        </h1>
        <p className="text-sm text-muted-foreground">
          Quản lý vòng đời tài khoản. Doc 55.
        </p>
      </header>

      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 text-destructive">
            <AlertTriangle className="size-4" /> Danger zone — Deactivate
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm space-y-2">
            <p>
              Deactivate tài khoản sẽ:
            </p>
            <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
              <li>
                Đăng xuất bạn khỏi <strong>tất cả thiết bị</strong> (revoke
                all sessions)
              </li>
              <li>Khoá tài khoản (`is_active=false`) — không login được nữa</li>
              <li>
                Giữ <strong>conversations / messages / data</strong> nguyên — admin
                có thể reactivate sau
              </li>
              <li>Audit log lại event này cho compliance</li>
            </ul>
            <p className="text-xs text-muted-foreground italic">
              Hard delete (xoá hoàn toàn data) defer v1.1 — yêu cầu liên hệ
              admin nếu cần ngay.
            </p>
          </div>

          <div className="space-y-2 pt-2 border-t">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-1"
              />
              <span>
                Tôi hiểu là tôi sẽ <strong>bị đăng xuất ngay</strong> và cần
                liên hệ admin để reactivate.
              </span>
            </label>

            <div>
              <label className="block text-sm font-medium mb-1">
                Nhập mật khẩu hiện tại để xác nhận
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mật khẩu native"
                disabled={submitting}
              />
            </div>

            {error ? (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-2 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            <Button
              variant="destructive"
              disabled={submitting || !acknowledged || password.length < 1}
              onClick={() => void handleDeactivate()}
            >
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin mr-1.5" /> Đang xử lý...
                </>
              ) : (
                <>
                  <ShieldOff className="size-4 mr-1.5" /> Deactivate tài khoản
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
